import React from 'react';
import { Loader2, ClipboardList, CheckCircle2, Bell, X, ArrowUpDown, CreditCard } from 'lucide-react';
import { moduleApi } from '../../lib/api';

interface Payment { date: string; amount: number; method: string; label: string; }

// Un item = une obligation financière d'un client Prestation, quelle que soit son origine (Démarche client
// payée en ligne, prestation clôturée manuellement dans le Planning, ou les deux combinées) — cf.
// GET /prestation-planning/payment-tracking, qui fusionne document_requests et prestation_appointments.
// Le statut ('bucket') ne porte que sur l'argent, jamais sur l'avancement du service :
//   - 'en_attente'  : rien réglé du tout
//   - 'partiel'     : un acompte est réglé (souvent en ligne), le reste est dû à la clôture de la prestation
//   - 'termine'     : totalité réglée
interface PaymentItem {
    id: string;
    source: 'demarche' | 'rdv';
    contact_label: string;
    label: string;
    sent_at: string | null;
    responded_at: string | null;
    service_date: string | null;
    total_due: number;
    total_paid: number;
    bucket: 'en_attente' | 'partiel' | 'termine';
    payments: Payment[];
    crm_email: string;
    appt_id: string | null;
    facture_id?: string | null;
}

const formatPrice = (amount: number) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(amount) || 0);
const formatDate = (d?: string | null) => d ? new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '';
const formatDateTime = (d?: string | null) => d ? new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

const TABS: { key: PaymentItem['bucket']; label: string; cls: string }[] = [
    { key: 'en_attente', label: 'En attente', cls: 'bg-warning-500/10 text-warning-500 border-warning-500/20' },
    { key: 'partiel', label: 'Partiellement payé', cls: 'bg-amber-500/10 text-amber-500 border-amber-500/20' },
    { key: 'termine', label: 'Terminé', cls: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' },
];

export const DocumentRequestsSettings: React.FC = () => {
    const [items, setItems] = React.useState<PaymentItem[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [activeTab, setActiveTab] = React.useState<PaymentItem['bucket']>('en_attente');
    const [sortDir, setSortDir] = React.useState<'asc' | 'desc'>('desc');
    const [selectedId, setSelectedId] = React.useState<string | null>(null);
    const [markingPaid, setMarkingPaid] = React.useState<string | null>(null);
    const [sendingReminderId, setSendingReminderId] = React.useState<string | null>(null);
    const [reminderResult, setReminderResult] = React.useState<{ id: string; ok: boolean; msg: string } | null>(null);

    const load = () => {
        moduleApi.getPrestationPaymentTracking().then((d: any) => setItems(Array.isArray(d) ? d : [])).catch(() => setItems([])).finally(() => setLoading(false));
    };
    React.useEffect(() => { load(); }, []);

    // Marque une Démarche client comme payée manuellement (paiement reçu autrement qu'en ligne) — génère la
    // facture si le module est actif, comme avant.
    const handleMarkPaid = async (id: string) => {
        setMarkingPaid(id);
        try {
            const res: any = await moduleApi.markDocumentRequestPaid(id);
            if (res?.facture_id && res?.invoice && res?.recipient_email) {
                try {
                    const { generateInvoicePdfFromData } = await import('../Factures/invoicePdfGenerator');
                    const { pdf } = await generateInvoicePdfFromData({ invoice: res.invoice, payload: res.payload, snapshot: res.snapshot });
                    const dataUri: string = pdf.output('datauristring');
                    const pdfBase64 = dataUri.split(',')[1];
                    await moduleApi.sendFactureEmail(res.facture_id, {
                        to: res.recipient_email, invoicePayload: res.payload, pdfBase64, filename: `${res.invoice?.invoice_number || 'facture'}.pdf`,
                    });
                } catch (pdfErr) {
                    console.error('Failed to generate/send invoice PDF', pdfErr);
                }
            }
            setSelectedId(null);
            load();
        } finally {
            setMarkingPaid(null);
        }
    };

    // Relance de paiement pour un solde restant (acompte réglé en ligne, reste dû à la clôture, ou prestation
    // clôturée manuellement encore impayée) — réutilise le même email "facture" déjà personnalisable dans
    // Paramètres du Planning > Notifications > Client > "Facture de fin de prestation", dont le corps
    // s'adapte automatiquement au solde restant.
    const handleSendReminder = async (item: PaymentItem) => {
        if (!item.appt_id) return;
        if (!item.crm_email) { setReminderResult({ id: item.id, ok: false, msg: 'Aucune adresse email renseignée pour ce client.' }); return; }
        setSendingReminderId(item.id);
        setReminderResult(null);
        try {
            let pdfBase64: string | undefined, filename: string | undefined;
            if (item.facture_id) {
                const factures: any[] = await moduleApi.getFactures();
                const invoice = (Array.isArray(factures) ? factures : []).find((f: any) => String(f.id) === String(item.facture_id));
                if (invoice) {
                    const payload = invoice.payload_json || {};
                    let snapshot = invoice.billing_snapshot || {};
                    if (!snapshot?.company_name) {
                        try {
                            const fresh = await moduleApi.getBillingSettings();
                            if (fresh?.company_name) snapshot = { ...fresh, ...snapshot };
                        } catch { /* ignore */ }
                    }
                    const { generateInvoicePdfFromData } = await import('../Factures/invoicePdfGenerator');
                    const { pdf } = await generateInvoicePdfFromData({ invoice, payload, snapshot });
                    const dataUri: string = pdf.output('datauristring');
                    pdfBase64 = dataUri.split(',')[1];
                    filename = `${invoice.invoice_number || 'facture'}.pdf`;
                }
            }
            const res: any = await moduleApi.sendPrestationInvoiceEmail(item.appt_id, { to: item.crm_email, pdfBase64, filename });
            if (res?.success) {
                setReminderResult({ id: item.id, ok: true, msg: 'Relance envoyée.' });
            } else {
                setReminderResult({ id: item.id, ok: false, msg: res?.error || 'Erreur lors de l\'envoi.' });
            }
        } catch {
            setReminderResult({ id: item.id, ok: false, msg: 'Erreur lors de l\'envoi.' });
        } finally {
            setSendingReminderId(null);
        }
    };

    if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-content-muted" size={24} /></div>;

    const filtered = items.filter((i) => i.bucket === activeTab);
    const sorted = [...filtered].sort((a, b) => {
        const diff = new Date(a.sent_at || 0).getTime() - new Date(b.sent_at || 0).getTime();
        return sortDir === 'asc' ? diff : -diff;
    });
    const selected = items.find((i) => i.id === selectedId) || null;
    const selectedRemaining = selected ? Math.max(0, selected.total_due - selected.total_paid) : 0;

    return (
        <div className="max-w-3xl space-y-4">
            <p className="text-xs text-content-muted italic">Suivi des paiements de vos clients Prestation — Démarches client payées en ligne et prestations clôturées dans le Planning, tous confondus.</p>

            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="bg-surface border border-border rounded-2xl p-1.5 inline-flex gap-1.5 flex-wrap">
                    {TABS.map((t) => (
                        <button key={t.key} onClick={() => setActiveTab(t.key)}
                            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${activeTab === t.key ? 'bg-brand-600 text-brand-fg' : 'text-content-muted hover:text-content'}`}>
                            {t.label} {items.filter((i) => i.bucket === t.key).length > 0 && `(${items.filter((i) => i.bucket === t.key).length})`}
                        </button>
                    ))}
                </div>
                <button onClick={() => setSortDir((d) => d === 'asc' ? 'desc' : 'asc')}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-border text-content-muted hover:text-content text-xs font-bold transition-all">
                    <ArrowUpDown size={13} /> {sortDir === 'asc' ? 'Plus anciennes d\'abord' : 'Plus récentes d\'abord'}
                </button>
            </div>

            {sorted.length === 0 ? (
                <div className="text-center py-16 border border-dashed border-border rounded-2xl">
                    <ClipboardList size={28} className="mx-auto mb-2 text-content-muted opacity-40" />
                    <p className="text-content-muted text-sm">Aucun paiement dans cette catégorie.</p>
                </div>
            ) : (
                <div className="space-y-2">
                    {sorted.map((item) => {
                        const remaining = Math.max(0, item.total_due - item.total_paid);
                        return (
                            <button key={item.id} onClick={() => { setSelectedId(item.id); setReminderResult(null); }}
                                className="w-full text-left p-4 rounded-2xl bg-surface-soft border border-border hover:border-brand-500/40 transition-all">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <p className="font-bold text-content text-sm">{item.contact_label} <span className="text-content-muted font-normal">— {item.label}</span></p>
                                        {item.sent_at && <p className="text-[11px] text-content-muted mt-0.5">{item.source === 'demarche' ? 'Envoyé' : 'Clôturé'} le {formatDate(item.sent_at)}</p>}
                                    </div>
                                    <span className={`shrink-0 text-[10px] font-black uppercase px-2 py-1 rounded-lg border ${TABS.find((t) => t.key === item.bucket)?.cls}`}>{TABS.find((t) => t.key === item.bucket)?.label}</span>
                                </div>
                                <div className="flex items-center gap-3 mt-1.5 text-xs text-content-muted">
                                    <span>Total : <strong className="text-content">{formatPrice(item.total_due)}</strong></span>
                                    {item.bucket !== 'en_attente' && <span>Réglé : <strong className="text-content">{formatPrice(item.total_paid)}</strong></span>}
                                    {remaining > 0 && <span>Reste : <strong className="text-content">{formatPrice(remaining)}</strong></span>}
                                </div>
                            </button>
                        );
                    })}
                </div>
            )}

            {selected && (
                <div className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-surface border border-border rounded-2xl w-full max-w-md max-h-[85vh] shadow-2xl overflow-hidden flex flex-col">
                        <div className="flex items-center justify-between p-5 border-b border-border shrink-0">
                            <div className="min-w-0">
                                <h3 className="text-base font-bold text-content truncate">{selected.contact_label}</h3>
                                <p className="text-xs text-content-muted truncate">{selected.label}</p>
                            </div>
                            <button onClick={() => setSelectedId(null)} className="p-1.5 text-content-muted hover:text-content rounded-lg hover:bg-surface-soft shrink-0"><X size={18} /></button>
                        </div>

                        <div className="p-5 space-y-4 overflow-y-auto">
                            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-content-muted">
                                {selected.source === 'demarche' && selected.sent_at && <span>Envoyé le {formatDateTime(selected.sent_at)}</span>}
                                {selected.responded_at && <span>Répondu le {formatDateTime(selected.responded_at)}</span>}
                                {selected.service_date && <span>Prestation le {formatDate(selected.service_date)}</span>}
                            </div>

                            <div className="p-3 rounded-xl bg-surface-soft border border-border space-y-1 text-sm">
                                <div className="flex items-center justify-between">
                                    <span className="text-content-muted">Total</span>
                                    <span className="font-bold text-content">{formatPrice(selected.total_due)}</span>
                                </div>
                                {selected.total_paid > 0 && (
                                    <div className="flex items-center justify-between text-xs">
                                        <span className="text-content-muted">Réglé</span>
                                        <span className="text-content-muted">{formatPrice(selected.total_paid)}</span>
                                    </div>
                                )}
                                {selectedRemaining > 0 && (
                                    <div className="flex items-center justify-between pt-1.5 border-t border-border font-bold">
                                        <span className="text-content-muted">Reste à payer</span>
                                        <span className="text-red-400">{formatPrice(selectedRemaining)}</span>
                                    </div>
                                )}
                            </div>

                            {selected.payments.length > 0 && (
                                <div className="space-y-1.5">
                                    <p className="text-[10px] font-black uppercase tracking-wider text-content-muted flex items-center gap-1.5"><CreditCard size={12} /> Historique des paiements</p>
                                    {selected.payments.map((p, i) => (
                                        <div key={i} className="flex items-center justify-between text-sm p-2.5 rounded-xl bg-surface-soft border border-border">
                                            <div className="min-w-0">
                                                <p className="text-content font-semibold">{p.label}</p>
                                                <p className="text-[11px] text-content-muted">{formatDateTime(p.date)}{p.method ? ` — ${p.method}` : ''}</p>
                                            </div>
                                            <span className="font-bold text-content shrink-0 ml-2">{formatPrice(p.amount)}</span>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {selected.bucket === 'termine' ? (
                                <p className="text-[11px] text-emerald-600 dark:text-emerald-400 font-semibold">Payé intégralement.</p>
                            ) : (
                                <div className="flex items-center gap-2 flex-wrap">
                                    {selected.source === 'demarche' && selected.total_paid <= 0 && (
                                        <button onClick={() => handleMarkPaid(selected.id)} disabled={markingPaid === selected.id}
                                            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold disabled:opacity-50 transition-all">
                                            <CheckCircle2 size={12} /> {markingPaid === selected.id ? 'Validation...' : 'Marquer comme payé'}
                                        </button>
                                    )}
                                    {selected.appt_id && (
                                        <button onClick={() => handleSendReminder(selected)} disabled={sendingReminderId === selected.id || !selected.crm_email}
                                            className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-border text-content-muted hover:text-content text-xs font-bold disabled:opacity-40 transition-all">
                                            <Bell size={12} /> {sendingReminderId === selected.id ? 'Envoi...' : 'Relancer pour le paiement'}
                                        </button>
                                    )}
                                </div>
                            )}
                            {!selected.crm_email && selected.appt_id && selected.bucket !== 'termine' && (
                                <p className="text-[11px] text-amber-500 font-semibold">Aucune adresse email renseignée pour ce client — impossible d'envoyer une relance.</p>
                            )}
                            {reminderResult?.id === selected.id && (
                                <p className={`text-xs font-semibold ${reminderResult.ok ? 'text-emerald-500' : 'text-red-500'}`}>{reminderResult.msg}</p>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
